/**
 * passport-cart-sync.js
 *
 * Keeps passport personalization properties in sync with line quantity.
 *
 * Problem (order #27536): the number of PasaporteN / ColorN / NombreN ... sets
 * is fixed at add-to-cart time. When a line's quantity changes afterwards
 * (cart +/- buttons, cart drawer, adding the same blank product twice so
 * Shopify merges the lines), the sets no longer match the number of physical
 * passports. The PDF system then receives too many or too few passports.
 *
 * Rule enforced here: for every passport line,
 * number of sets === quantity x passports per unit.
 *   - plain passport products (My Travel Passport World, INDIVIDUAL / PAREJA /
 *     FAMILIA variants): 1 passport per unit, so sets === quantity
 *   - pack products (Pack Duo, Trio, Traveler, Atlantic, Wanderlust, Family
 *     Pack...): 1 unit = N passports, N read from the title "(N Pasaportes)"
 *   - expected > sets  -> append blank sets (same edition as set 1)
 *   - expected < sets  -> drop the highest-numbered sets, but ONLY if they are
 *     blank; a set with a name, surname, nationality, phrase, date or image is
 *     never deleted
 *
 * Bug fixed 2026-10-01: the first version assumed 1 passport per unit for every
 * line, so a Pack Duo (quantity 1, 2 sets) lost Pasaporte2..Imagen2 before
 * checkout (orders #27553 #27559 #27562 #27572 #27578 #27581 #27582).
 *
 * Bug fixed 2026-10-09 (orders #27669 #27662): the automatic Buy X get Y
 * discount "120Euro-gift" (spend 120 EUR, get Passport Quest free) makes
 * Shopify SPLIT one cart line into several /cart.js items (e.g. FAMILIA qty 4
 * -> qty 3 + qty 1, the 3 units that reach 120 EUR and the rest). The split
 * items have different keys but SHARE the same properties: changing one changes
 * the other. The old code checked each item on its own (qty 1 wants 1 set,
 * qty 3 wants 3 sets), so it rewrote the shared properties back and forth, the
 * /cart page reloaded in a loop, personalization of passports 2..N was wiped,
 * and the quantity could even grow (4 -> 7 in a test). Now items with the same
 * variant + same properties are treated as ONE logical line whose quantity is
 * the sum of the split items; a consistent split line is left untouched.
 * Note: the order still shows the split items, each with the full set list.
 * Fulfilment must count those identical lines once (total qty = sum).
 *
 * Runs: on page load, after every Dawn cartUpdate event, and right before
 * checkout (checkout is blocked until the sync finishes).
 */
(function () {
  'use strict';
  if (window.PassportCartSyncInitialized) return;
  window.PassportCartSyncInitialized = true;

  var KEYS = ['Pasaporte', 'Color', 'Nombre', 'Apellidos', 'Nacionalidad', 'Fecha', 'Modalidad', 'Frase', 'Imagen'];
  var KEY_RE = new RegExp('^(' + KEYS.join('|') + ')(\\d+)$');
  var BLANK_KEYS = ['Nombre', 'Apellidos', 'Nacionalidad', 'Frase'];
  /** Keys whose value means the shopper personalized that passport. */
  var PERSONAL_KEYS = ['Nombre', 'Apellidos', 'Nacionalidad', 'Frase', 'Fecha', 'Imagen'];
  var MAX_PASSPORTS = 25;
  var MAX_PASSES = 10;
  var RELOAD_GUARD_KEY = 'passportCartSyncReloadAt';
  var RELOAD_GUARD_MS = 15000;
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  var running = null;
  var debounceTimer = null;

  function randomColorLabel() {
    var el = document.getElementById('passport-customizer-modal');
    return (el && el.getAttribute('data-random-color-label')) || 'Aleatorio';
  }

  /**
   * Same rule as snippets/cart-drawer.liquid `is_compact_pack`. /cart.js returns
   * translated titles (es/pt COMPACTO, en COMPACT, fr COMPACTE, de KOMPAKT,
   * it COMPATTO), so "compact" alone misses DE/IT carts (order #27720).
   */
  var COMPACT_RE = /compact|kompakt|compatt/i;
  function isCompact(item) {
    var t = [item.product_title, item.variant_title, item.title]
      .concat((item.options_with_values || []).map(function (o) { return o.value; }))
      .join(' ')
      .toLowerCase();
    return COMPACT_RE.test(t);
  }

  /** Highest N found in PasaporteN keys, or 0 when the line is not a passport line. */
  function countSets(props) {
    var max = 0;
    Object.keys(props || {}).forEach(function (k) {
      var m = k.match(KEY_RE);
      if (m && m[1] === 'Pasaporte') max = Math.max(max, parseInt(m[2], 10));
    });
    return max;
  }

  /** True when passport set n carries anything the shopper typed or uploaded. */
  function isPersonalized(props, n) {
    return PERSONAL_KEYS.some(function (key) {
      var v = props[key + n];
      return v != null && String(v).trim() !== '';
    });
  }

  /**
   * Passports in ONE unit of this line. Every pack product carries the count in
   * its title in all locales: "(2 Pasaportes)", "(3 Passports)", "(2 Pasaportes
   * en 1 Caja)"... Single-passport products have no "(N " in the title.
   * Checked 2026-10-01: 40 multi-passport packs, consistent in es/en/de/fr/it/pt.
   */
  function passportsPerUnit(item) {
    var m = String(item.product_title || '').match(/\((\d+)\s/);
    var n = m ? parseInt(m[1], 10) : 1;
    return n >= 1 && n <= MAX_PASSPORTS ? n : 1;
  }

  /** Stable signature of a properties object (key order independent). */
  function propsSignature(props) {
    var keys = Object.keys(props || {}).sort();
    return JSON.stringify(keys.map(function (k) { return [k, props[k]]; }));
  }

  /**
   * Groups /cart.js items into logical lines. Items that Shopify split for a
   * discount allocation share variant, selling plan and properties; their
   * quantities belong to one line and one set list.
   */
  function groupItems(items) {
    var groups = [];
    var byId = {};
    items.forEach(function (item) {
      var plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
        ? item.selling_plan_allocation.selling_plan.id
        : '';
      var id = item.variant_id + '|' + plan + '|' + propsSignature(item.properties);
      if (!byId[id]) {
        byId[id] = { first: item, parts: [], quantity: 0, sellingPlan: plan };
        groups.push(byId[id]);
      }
      byId[id].parts.push(item);
      byId[id].quantity += item.quantity;
    });
    return groups;
  }

  /** Returns new properties with exactly quantity x passportsPerUnit sets, or null if nothing to change (or not safe to change). */
  function buildProperties(item, qty) {
    var props = item.properties || {};
    var sets = countSets(props);
    if (!sets || qty < 1) return null;
    var expected = qty * passportsPerUnit(item);
    if (expected > MAX_PASSPORTS || sets === expected) return null;

    if (sets > expected) {
      for (var d = expected + 1; d <= sets; d++) {
        if (isPersonalized(props, d)) {
          console.warn('[passport-cart-sync] not removing personalized passport ' + d + ' from "' + item.product_title + '" (' + sets + ' sets, quantity ' + qty + ')');
          return null;
        }
      }
    }

    var next = {};
    Object.keys(props).forEach(function (k) {
      var m = k.match(KEY_RE);
      if (m && parseInt(m[2], 10) > expected) return; // drop blank sets above expected
      next[k] = props[k];
    });

    var edition = props.Pasaporte1 || '';
    var color = isCompact(item) && props.Color1 ? props.Color1 : randomColorLabel();
    for (var n = sets + 1; n <= expected; n++) {
      next['Pasaporte' + n] = edition;
      next['Color' + n] = color;
      BLANK_KEYS.forEach(function (key) { next[key + n] = ''; });
    }
    return next;
  }

  function getCart() {
    return fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); });
  }

  function postJson(path, body) {
    return fetch(ROOT + path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error(path + ' ' + r.status);
      return r.json();
    });
  }

  /**
   * Target the line by its key, never by its 1-based position: other apps
   * (BOGOS free gift, upsells) insert lines at the top of the cart between our
   * cart.js read and this request, and a stale position would write one line's
   * passports onto a different line.
   */
  function changeLine(key, quantity, properties) {
    return postJson('cart/change.js', { id: key, quantity: quantity, properties: properties });
  }

  /**
   * A split line cannot be edited part by part (the parts share properties).
   * Remove every part, then add the line back once with the total quantity and
   * the corrected properties. Shopify may split it again for the discount; the
   * parts then share a set list that matches the total, so nothing else runs.
   */
  function replaceGroup(group, properties) {
    var updates = {};
    group.parts.forEach(function (part) { updates[part.key] = 0; });
    var line = { id: group.first.variant_id, quantity: group.quantity, properties: properties };
    if (group.sellingPlan) line.selling_plan = group.sellingPlan;
    return postJson('cart/update.js', { updates: updates }).then(function () {
      return postJson('cart/add.js', { items: [line] }).catch(function (e) {
        // Put the original line back rather than lose it.
        var restore = { id: group.first.variant_id, quantity: group.quantity, properties: group.first.properties };
        if (group.sellingPlan) restore.selling_plan = group.sellingPlan;
        return postJson('cart/add.js', { items: [restore] }).then(function () { throw e; });
      });
    });
  }

  /** One logical line at a time, re-reading the cart after each change (lines can merge, split or reorder). */
  function syncOnce() {
    var changed = false;
    function pass(i) {
      if (i >= MAX_PASSES) return Promise.resolve(changed);
      return getCart().then(function (cart) {
        var groups = groupItems(cart.items || []);
        for (var g = 0; g < groups.length; g++) {
          var group = groups[g];
          var props = buildProperties(group.first, group.quantity);
          if (!props) continue;
          changed = true;
          var write = group.parts.length === 1
            ? changeLine(group.first.key, group.quantity, props)
            : replaceGroup(group, props);
          return write.then(function () { return pass(i + 1); });
        }
        return changed;
      });
    }
    return pass(0);
  }

  /** Reload /cart at most once per RELOAD_GUARD_MS so a sync can never loop the page. */
  function canReload() {
    try {
      var last = parseInt(window.sessionStorage.getItem(RELOAD_GUARD_KEY) || '0', 10);
      if (Date.now() - last < RELOAD_GUARD_MS) return false;
      window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
      return true;
    } catch (e) {
      return false;
    }
  }

  function refreshCartUi() {
    if (/\/cart\/?$/.test(window.location.pathname)) {
      if (canReload()) window.location.reload();
      return;
    }
    // Re-render the drawer body in place (like CartDrawerItems) without
    // calling CartDrawer.renderContents, which would also open the drawer.
    var inner = document.querySelector('cart-drawer .drawer__inner');
    if (!inner) return;
    fetch(ROOT + '?sections=cart-drawer')
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        var html = sections && sections['cart-drawer'];
        if (!html) return;
        var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.drawer__inner');
        if (fresh) inner.innerHTML = fresh.innerHTML;
      })
      .catch(function () {});
  }

  /** Public: resolves when the cart is consistent. Never rejects. */
  function sync() {
    if (running) return running;
    running = syncOnce()
      .then(function (changed) { if (changed) refreshCartUi(); return changed; })
      .catch(function (e) { console.warn('[passport-cart-sync]', e); return false; })
      .then(function (changed) { running = null; return changed; });
    return running;
  }
  window.syncPassportCart = sync;

  function scheduleSync() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(sync, 400);
  }

  // 1. On load
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scheduleSync);
  else scheduleSync();

  // 2. After every cart change made by the theme (Dawn pub/sub)
  if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
    window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, scheduleSync);
  }

  // 3. Block checkout until the cart is consistent
  document.addEventListener('click', function (event) {
    var btn = event.target.closest && event.target.closest('[name="checkout"], a[href$="/checkout"], a[href*="/checkout?"]');
    if (!btn || btn.dataset.passportSynced === 'true') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    btn.setAttribute('aria-disabled', 'true');
    sync().then(function () {
      btn.dataset.passportSynced = 'true';
      btn.removeAttribute('aria-disabled');
      if (btn.form && btn.name === 'checkout') {
        var hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'checkout';
        btn.form.appendChild(hidden);
        btn.form.submit();
      } else if (btn.href) {
        window.location.href = btn.href;
      } else {
        window.location.href = ROOT + 'checkout';
      }
    });
  }, true);
})();
